import {
    Form,
    useForm,
    IDomainInfo,
    IThankYouOffer,
    useFormContext,
    ICategory,
} from "@severalbrands/form-fusion";
import { useQuery } from "@tanstack/react-query";
import { Session } from "src/api/session";
import styles from "./styles.module.scss";
import MarketingMessage from "./MarketingMessage";
import { GQLCategories } from "@gql/types/gql";
import { ICustomDomain, Image } from "@genericTypes/sharedTypes";
import { useEffect } from "react";
import useWindowSize from "@hooks/useWindowSize";
import CloseModal from "./CloseModal";
import LegacyForm from "@components/shared/form";

const hasValidFormFusionPayload = (form: unknown): boolean => {
    if (!form || typeof form !== "object" || Array.isArray(form)) {
        return false;
    }

    const maybeForm = form as { steps?: unknown[] };

    return Array.isArray(maybeForm.steps) && maybeForm.steps.length > 0;
};

const FormFusion = ({ category }: { category?: GQLCategories | null }) => {
    const { resetForm, getTotalSteps } = useForm();
    const { state } = useFormContext();
    const { width } = useWindowSize();
    const { data: session } = useQuery<Session>({ queryKey: ["session"] });
    const { data: formFusionForm } = useQuery({ queryKey: ["formFusionForm"] });
    const { data: formFusionThankYouOffers } = useQuery<IThankYouOffer[]>({
        queryKey: ["formFusionThankYouOffers"],
    });
    const { data } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });

    const domainInfo: IDomainInfo = {
        name: data?.domain.name as string | undefined,
        niceName: data?.domain.niceName as string,
        textBlocks: [],
        id: data?.domain.id as number,
        defaultOffers: formFusionThankYouOffers ?? [],
        logo: data?.domain.logo as Image,
    };

    const formFusionCategory: ICategory = {
        id: category?.id as number,
        name: category?.name as string,
        slug: category?.slug as string,
        label: category?.label as string | null,
        icon: null,
        image: null,
        title: category?.title as string | null,
        slugAlias: category?.slugAlias as string,
        crossSellingDomainFormId: null,
        description: null,
        headline: null,
        metatags: [],
        modelType: "",
        hasCrossSelling: false,
        translations: [],
        isOneToOneConsent: category?.isOneToOneConsent as boolean | undefined,
    };

    useEffect(() => {
        if (state.isFormSubmitted) {
            resetForm();
        }
    }, [state.showModal]);

    if (!hasValidFormFusionPayload(formFusionForm)) {
        return <LegacyForm shouldSplit />;
    }

    return (
        <div className="w-full">
            <Form
                // @ts-ignore
                form={formFusionForm}
                // @ts-ignore
                visitDetails={session?.visitDetails}
                category={formFusionCategory}
                domainInfo={domainInfo}
                styles={{
                    formContainer: styles["form-container"],
                    stepDescription: styles["step-description"],
                    submitButton: styles["submit-button"],
                    select: styles["select"],
                    selectContainer: styles["select-container"],
                    modalContainer: styles["modal-container"],
                    radioOption: styles["radio-option"],
                    radioIconContainer: styles["radio-icon-container"],
                    modalContentContainer: styles["modal"],
                    modalCloseButton: styles["modal-close-button"],
                    offerContainer: styles["offer-container"],
                    offerImage: styles["offer-image"],
                    invalidField: styles["invalid-field"],
                    thankYouContainer: styles["thank-you-container"],
                    stepTitleContainer: styles["step-title-container"],
                    offerButton: styles["offer-button"],
                    formFooterContainer: styles["form-footer"],
                    input: styles["input"],
                    stepTitle: [
                        {
                            className: styles["step-title"],
                            condition: (state) => state.currentStep === 0,
                        },
                        {
                            className: styles["step-title-all"],
                            condition: (state) => state.currentStep > 0,
                        },
                    ],
                    modalChildren: [
                        {
                            className: styles["modal-children"],
                            condition: (state) =>
                                state.currentStep !== getTotalSteps() - 1,
                        },
                        {
                            className: styles["modal-children-last-step"],
                            condition: (state) => {
                                return (
                                    state.currentStep === getTotalSteps() - 1
                                );
                            },
                        },
                    ],
                    modalInnerContainer: [
                        {
                            className: styles["modal-inner-container"],
                            condition: () =>
                                state.currentStep === getTotalSteps() - 1,
                        },
                    ],
                    stepWrapperContainer: [
                        {
                            className: styles["step-wrapper-container"],
                            condition: (state): boolean =>
                                state.currentStep > 0 && !state.isFormSubmitted,
                        },
                        {
                            className: styles["step-wrapper"],
                        },
                    ],
                    form: [
                        {
                            className: styles["form-fusion"],
                            condition: (state): boolean =>
                                state.currentStep === 0,
                        },
                        {
                            className: styles["form"],
                            condition: (state): boolean =>
                                state.currentStep > 0,
                        },
                        {
                            className: styles["form-container-on-thank-you"],
                            condition: (state): boolean =>
                                state.isFormSubmitted,
                        },
                    ],
                    progressBar: [
                        {
                            className: styles["progress-bar"],
                            condition: (state): boolean =>
                                state.currentStep === 0,
                        },
                    ],
                    field: [
                        {
                            className: styles["field"],
                            condition: (state): boolean =>
                                state.currentStep === 0,
                        },
                    ],
                    fieldLabel: [
                        {
                            className: styles["field-label"],
                            condition: (state): boolean =>
                                state.currentStep === 0,
                        },
                    ],
                }}
                uiConfig={{
                    badge: {
                        element: "Get a Free Quote",
                        hide(state) {
                            return state.isFormSubmitted
                                ? true
                                : state.currentStep !== 0;
                        },
                    },
                    closeModal: {
                        element:
                            state.isFormSubmitted || width < 1024 ? (
                                <CloseModal />
                            ) : undefined,
                        position:
                            state.isFormSubmitted || width < 1024
                                ? "bottom"
                                : "top",
                    },
                    marketingMessage: {
                        element:
                            state.isFormSubmitted ||
                            width < 1024 ? undefined : (
                                <MarketingMessage />
                            ),
                    },
                    submitButtonArrow: {
                        hide: (state) => state.currentStep > 0,
                    },
                }}
                modal
                thankYouOffersPosition="bottom"
                thankYouOfferAspect="square"
            />
        </div>
    );
};

export default FormFusion;
